import { compact, uniqBy } from 'lodash';
import { useEffect, useRef } from 'react';

import { getPresentationTemplateName } from '@/modules/presentationTemplates/presentationTemplate.utils';

import { usePresentationStepContext } from '../PresentationTemplateSelectionStep.context';
import { OnboardingPresentationTemplate } from '../PresentationTemplateSelectionStep.utils';

interface UsePresentationTemplateSelectionOptions {
  pinnedTemplates: OnboardingPresentationTemplate[];
  availableTemplates: OnboardingPresentationTemplate[];
  loading: boolean;
}

export function usePresentationTemplateSelection({
  pinnedTemplates,
  availableTemplates,
  loading,
}: UsePresentationTemplateSelectionOptions) {
  const {
    selectedTemplateIds,
    setSelectedTemplateIds,
    completeOnboarding,
    isCompleting,
  } = usePresentationStepContext();
  const hasSetInitialSelection = useRef(false);
  const displayedTemplates =
    pinnedTemplates.length > 0 ? pinnedTemplates : availableTemplates;
  const pinnedTemplateIds = new Set(pinnedTemplates.map(({ id }) => id));
  const pinnedSystemTemplateKinds = new Set(
    compact(pinnedTemplates.map(({ systemTemplateKind }) => systemTemplateKind))
  );
  const otherTemplates = uniqBy(
    availableTemplates.filter(
      ({ id, systemTemplateKind }) =>
        !pinnedTemplateIds.has(id) &&
        (!systemTemplateKind ||
          !pinnedSystemTemplateKinds.has(systemTemplateKind))
    ),
    ({ id, systemTemplateKind }) =>
      systemTemplateKind ? `system:${systemTemplateKind}` : `template:${id}`
  );
  const selectedOtherTemplateIds = selectedTemplateIds.filter(
    (id) => !pinnedTemplateIds.has(id)
  );

  useEffect(() => {
    if (
      hasSetInitialSelection.current ||
      loading ||
      selectedTemplateIds.length > 0 ||
      pinnedTemplates.length !== 1
    )
      return;

    const onlyPinnedTemplate = pinnedTemplates[0];
    if (!onlyPinnedTemplate) return;

    hasSetInitialSelection.current = true;
    setSelectedTemplateIds([onlyPinnedTemplate.id]);
  }, [
    loading,
    pinnedTemplates,
    selectedTemplateIds.length,
    setSelectedTemplateIds,
  ]);

  const toggleTemplate = (templateId: string) => {
    if (selectedTemplateIds.includes(templateId)) {
      setSelectedTemplateIds(
        selectedTemplateIds.filter((id) => id !== templateId)
      );
      return;
    }
    setSelectedTemplateIds([...selectedTemplateIds, templateId]);
  };

  const selectOtherTemplates = (otherTemplateIds: string[]) => {
    const selectedPinnedTemplateIds = selectedTemplateIds.filter((id) =>
      pinnedTemplateIds.has(id)
    );
    setSelectedTemplateIds([...selectedPinnedTemplateIds, ...otherTemplateIds]);
  };

  return {
    completeOnboarding,
    displayedTemplates,
    isCompleting,
    options: otherTemplates.map((template) => ({
      display: getPresentationTemplateName(template),
      value: template.id,
    })),
    selectOtherTemplates,
    selectedOtherTemplateIds,
    selectedTemplateIds,
    toggleTemplate,
  };
}
